import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useTranslation } from "react-i18next";
import { header, people } from "../../config/imagesLink";
import { useEffectiveTheme } from "../../hooks/useEffectiveTheme";
import "../../styles/authSplit.css";

const AuthLangToggle = React.forwardRef(function AuthLangToggle(
  { children, onClick, className, ...props },
  ref
) {
  return (
    <button
      type="button"
      {...props}
      ref={ref}
      className="auth-icon-btn"
      onClick={(event) => {
        event.preventDefault();
        if (onClick) onClick(event);
      }}
    >
      {children}
    </button>
  );
});

const AuthSplitLayout = ({
  children,
  switchPrompt,
  switchTo,
  switchLabel,
  connectionList,
  onGuestNavigate,
  currentLang,
  onChangeLanguage,
  onProfileImageError,
  plainRight = false,
}) => {
  const { t } = useTranslation();
  const isDark = useEffectiveTheme();

  const toggleTheme = () => {
    window.dispatchEvent(
      new CustomEvent("wlj-theme-change", { detail: { isDark: !isDark } })
    );
  };

  const handleProfileError = (e) => {
    if (onProfileImageError) {
      onProfileImageError(e);
      return;
    }
    if (!e || !e.target) return;
    e.target.onerror = null;
    e.target.src = people.DefaultProfile;
  };

  const renderLanguageMenu = () => (
    <Dropdown.Menu align="end">
      {[
        { code: "en", label: "English" },
        { code: "hi", label: "Hindi" },
        { code: "ar", label: "Arabic" },
      ].map((lang) => (
        <Dropdown.Item
          key={lang.code}
          as="button"
          style={{ width: "150px" }}
          onClick={() => onChangeLanguage(lang.code)}
        >
          <div className="d-flex justify-content-between align-items-center w-100">
            <span>{lang.label}</span>
            {currentLang === lang.code && (
              <span style={{ color: "#0d6efd", fontWeight: 600 }}>✓</span>
            )}
          </div>
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  );

  const features = [
    {
      icon: <VerifiedOutlinedIcon />,
      tone: "is-blue",
      title: t("SignIn.100% Verified", { defaultValue: "100% Verified" }),
      desc: t("SignIn.Genuine opportunities", { defaultValue: "Genuine opportunities." }),
    },
    {
      icon: <GroupsOutlinedIcon />,
      tone: "is-purple",
      title: t("SignIn.15K+ Active Users", { defaultValue: "15K+ Active Users" }),
      desc: t("SignIn.Join a trusted community", { defaultValue: "Join a trusted community." }),
    },
    {
      icon: <WorkOutlineOutlinedIcon />,
      tone: "is-green",
      title: t("Sidemenubar.Top Companies"),
      desc: t("SignIn.Find the right place to grow", { defaultValue: "Find the right place to grow." }),
    },
    {
      icon: <ShieldOutlinedIcon />,
      tone: "is-orange",
      title: t("SignIn.Secure & Safe", { defaultValue: "Secure & Safe" }),
      desc: t("SignIn.Your data is always protected", { defaultValue: "Your data is always protected." }),
    },
  ];

  return (
    <div className="container-fluid auth-split">
      <div className="auth-split-scrim" aria-hidden="true" />
      <div className="row auth-split-row">
        <div className="col-lg-7 auth-split-left loginImage">
          <div className="auth-split-left-inner">
          <div className="auth-split-left-top">
            <img src={header.mainLogo} alt="Welink Jobs" className="auth-split-logo" />
            <div className="auth-split-toolbar">
              <button
                type="button"
                className="auth-icon-btn"
                onClick={toggleTheme}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                title={isDark ? "Light mode" : "Dark mode"}
              >
                {isDark ? (
                  <LightModeOutlinedIcon sx={{ fontSize: 20 }} />
                ) : (
                  <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />
                )}
              </button>
              <Dropdown className="auth-split-lang" align="end">
                <Dropdown.Toggle
                  as={AuthLangToggle}
                  id="language-toggle"
                  aria-label="Change language"
                  title="Language"
                >
                  <LanguageOutlinedIcon sx={{ fontSize: 20 }} />
                </Dropdown.Toggle>
                {renderLanguageMenu()}
              </Dropdown>
            </div>
          </div>

          <div className="auth-split-left-body">
            <h1 className="auth-split-headline">
              {t("SignIn.Stay connected for")}{" "}
              <span className="auth-accent-wrap">
                <NavLink to="/SearchJob" onClick={onGuestNavigate} className="auth-accent">
                  {t("Company Profile.Jobs")}
                </NavLink>
                {" "}
                {t("SignIn.and")}{" "}
                <NavLink to="/NewsFeed" onClick={onGuestNavigate} className="auth-accent">
                  {t("SignIn.More")}
                </NavLink>
              </span>
            </h1>
            <p className="auth-split-sub">
              {t("SignIn.Join thousands growing", {
                defaultValue: "Join thousands of professionals and companies already growing with WelinkJobs.",
              })}
            </p>

            <div className="auth-feature-list">
              {features.map((feature) => (
                <div className="auth-feature" key={feature.title}>
                  <span className={`auth-feature-icon ${feature.tone}`}>{feature.icon}</span>
                  <span className="auth-feature-copy">
                    <span className="auth-feature-title">{feature.title}</span>
                    <span className="auth-feature-desc">{feature.desc}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="auth-split-social-proof">
            <Link to="/People" onClick={onGuestNavigate} className="auth-split-avatars">
              <div className="circle-container">
                {connectionList &&
                  connectionList.slice(0, 4).map((user, userIndex) => (
                    <img
                      src={user?.profilPic}
                      alt=""
                      className="circle-img"
                      key={user?.id || userIndex}
                      onError={handleProfileError}
                    />
                  ))}
              </div>
              <span className="auth-split-count">+15k</span>
            </Link>
            <span>{t("SignIn.15k+ people joined us, now it's your turn")}</span>
          </div>
          </div>
        </div>

        <div className={`col-lg-5 auth-split-right Login-Section SignUpSec${plainRight ? " is-plain" : ""}`}>
          <div className="auth-mobile-shell">
            <div className="auth-mobile-shell-top">
              <div className="auth-mobile-brand">
                <img src={header.mainLogo} alt="Welink Jobs" className="auth-mobile-brand-logo" />
                <p className="auth-mobile-brand-tag">
                  {t("SignIn.Stay connected for")}{" "}
                  <span className="auth-mobile-accent">
                    {t("Company Profile.Jobs")} & {t("SignIn.More")}
                  </span>
                </p>
              </div>
              <div className="auth-split-toolbar auth-split-toolbar-mobile">
                <button
                  type="button"
                  className="auth-icon-btn"
                  onClick={toggleTheme}
                  aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                  title={isDark ? "Light mode" : "Dark mode"}
                >
                  {isDark ? (
                    <LightModeOutlinedIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />
                  )}
                </button>
                <Dropdown className="auth-split-lang auth-split-lang-mobile" align="end">
                  <Dropdown.Toggle
                    as={AuthLangToggle}
                    id="language-toggle-mobile"
                    aria-label="Change language"
                    title="Language"
                  >
                    <LanguageOutlinedIcon sx={{ fontSize: 20 }} />
                  </Dropdown.Toggle>
                  {renderLanguageMenu()}
                </Dropdown>
              </div>
            </div>

            {plainRight ? (
              <div className="auth-plain-right">
                <img src={header.mainLogo} alt="Welink Jobs" className="auth-mobile-logo auth-mobile-logo-in-card" />
                {children}
              </div>
            ) : (
              <div className="auth-form-card">
                <p className="auth-split-switch">
                  {switchPrompt} <Link to={switchTo}>{switchLabel}</Link>
                </p>
                <img src={header.mainLogo} alt="Welink Jobs" className="auth-mobile-logo auth-mobile-logo-in-card" />
                {children}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthSplitLayout;
